import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
} from '@/components/ui/select';
import { AnchoredDropdownMenu } from '@/components/ui/AnchoredDropdownMenu';
import { cn } from '@/lib/utils';
import {
	PROFESSIONAL_PERMISSION_TAG_FILTER_OPTIONS,
	parsePermissionTagsFromSearchParams,
} from '@/utils/professionalPermissionTags';

const FILTER_ALL_VALUE = '__all__';

const COMPACT_FIELD_CLASS = 'w-[10.5rem] shrink-0';
const COMPACT_SELECT_TRIGGER_CLASS =
	'h-9 rounded-xl border-stone-200 bg-white px-3 text-xs font-semibold text-zinc-600 shadow-sm';
const COMPACT_LABEL_CLASS =
	'mb-1.5 block text-[10px] font-bold uppercase tracking-wider text-slate-500';

interface CompactSelectFilterProps {
	label: string;
	paramKey: string;
	options: Array<{ value: string; label: string }>;
	value: string;
	onChange: (paramKey: string, value: string) => void;
}

const CompactSelectFilter = ({
	label,
	paramKey,
	options,
	value,
	onChange,
}: CompactSelectFilterProps) => (
	<div className={COMPACT_FIELD_CLASS}>
		<label className={COMPACT_LABEL_CLASS}>{label}</label>
		<Select
			value={value || FILTER_ALL_VALUE}
			onValueChange={(next) => onChange(paramKey, next)}
		>
			<SelectTrigger
				className={COMPACT_SELECT_TRIGGER_CLASS}
				hideTriggerIcon
			>
				<span
					className={cn(
						'truncate',
						!value && 'font-normal text-zinc-400',
					)}
				>
					{value
						? options.find((option) => option.value === value)
								?.label
						: 'Todos'}
				</span>
				<ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50" />
			</SelectTrigger>
			<SelectContent className="bg-white">
				<SelectItem value={FILTER_ALL_VALUE} indicator={false}>
					Todos
				</SelectItem>
				{options.map((option) => (
					<SelectItem
						key={option.value}
						value={option.value}
						indicator={false}
					>
						{option.label}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	</div>
);

interface ProfessionalAdminFiltersProps {
	services: Array<{
		id: string;
		professional_label?: string | null;
		service_name: string;
	}>;
}

const ProfessionalAdminFilters = ({
	services,
}: ProfessionalAdminFiltersProps) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [tagsMenuOpen, setTagsMenuOpen] = useState(false);

	const authorized = searchParams.get('authorized') ?? '';
	const contract = searchParams.get('contract') ?? '';
	const serviceModality = searchParams.get('service_modality') ?? '';
	const serviceId = searchParams.get('service_id') ?? '';
	const selectedTags = useMemo(
		() => parsePermissionTagsFromSearchParams(searchParams),
		[searchParams],
	);

	const serviceOptions = useMemo(
		() =>
			services.map((service) => ({
				value: service.id,
				label: service.professional_label || service.service_name,
			})),
		[services],
	);

	const handleSimpleFilterChange = useCallback(
		(paramKey: string, value: string) => {
			const nextParams = new URLSearchParams(searchParams.toString());
			if (value === '' || value === FILTER_ALL_VALUE) {
				nextParams.delete(paramKey);
			} else {
				nextParams.set(paramKey, value);
			}
			setSearchParams(nextParams, { replace: true });
		},
		[searchParams, setSearchParams],
	);

	const handleToggleTag = useCallback(
		(tag: Professionals.PermissionTag) => {
			const nextParams = new URLSearchParams(searchParams.toString());
			nextParams.delete('permission_tags');

			const nextTags = selectedTags.includes(tag)
				? selectedTags.filter((current) => current !== tag)
				: [...selectedTags, tag];

			for (const currentTag of nextTags) {
				nextParams.append('permission_tags', currentTag);
			}

			setSearchParams(nextParams, { replace: true });
		},
		[searchParams, selectedTags, setSearchParams],
	);

	const tagsTriggerLabel = useMemo(() => {
		if (selectedTags.length === 0) return 'Todos';
		if (selectedTags.length === 1) {
			return (
				PROFESSIONAL_PERMISSION_TAG_FILTER_OPTIONS.find(
					(option) => option.value === selectedTags[0],
				)?.label ?? selectedTags[0]
			);
		}
		return `${selectedTags.length} tags`;
	}, [selectedTags]);

	return (
		<div className="flex flex-wrap items-end gap-3">
			<CompactSelectFilter
				label="Autorizado"
				paramKey="authorized"
				value={authorized}
				onChange={handleSimpleFilterChange}
				options={[
					{ value: 'true', label: 'Sim' },
					{ value: 'false', label: 'Não' },
				]}
			/>
			<CompactSelectFilter
				label="Contrato"
				paramKey="contract"
				value={contract}
				onChange={handleSimpleFilterChange}
				options={[
					{ value: 'INDEPENDENT', label: 'Independente' },
					{ value: 'PARTNER', label: 'Parceria' },
				]}
			/>
			<CompactSelectFilter
				label="Modalidade"
				paramKey="service_modality"
				value={serviceModality}
				onChange={handleSimpleFilterChange}
				options={[
					{ value: 'FULL_REMOTE', label: 'Online' },
					{ value: 'HYBRID', label: 'Híbrido' },
					{ value: 'FULL_PRESENCIAL', label: 'Presencial' },
				]}
			/>
			{serviceOptions.length > 0 && (
				<CompactSelectFilter
					label="Serviço"
					paramKey="service_id"
					value={serviceId}
					onChange={handleSimpleFilterChange}
					options={serviceOptions}
				/>
			)}
			<div className={cn(COMPACT_FIELD_CLASS, 'w-[11.5rem]')}>
				<label className={COMPACT_LABEL_CLASS}>Tags de permissão</label>
				<AnchoredDropdownMenu
					open={tagsMenuOpen}
					onOpenChange={setTagsMenuOpen}
					menuClassName="animate-in fade-in zoom-in-95 w-[13.5rem] duration-100"
					trigger={({ ref, isOpen, onTriggerClick }) => (
						<button
							ref={ref}
							type="button"
							onClick={onTriggerClick}
							className={cn(
								COMPACT_SELECT_TRIGGER_CLASS,
								'flex w-full items-center gap-2 border border-solid',
								isOpen
									? 'border-zinc-400 ring-2 ring-stone-900/5'
									: 'border-stone-200',
							)}
							aria-expanded={isOpen}
							aria-haspopup="menu"
						>
							<span
								className={cn(
									'truncate text-left',
									selectedTags.length === 0 &&
										'font-normal text-zinc-400',
								)}
							>
								{tagsTriggerLabel}
							</span>
							<ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 opacity-50" />
						</button>
					)}
				>
					<div className="flex flex-col py-1">
						{PROFESSIONAL_PERMISSION_TAG_FILTER_OPTIONS.map(
							(option) => {
								const checked = selectedTags.includes(
									option.value,
								);
								return (
									<button
										key={option.value}
										type="button"
										onClick={() =>
											handleToggleTag(option.value)
										}
										className="flex items-center gap-2.5 px-3 py-2.5 text-left text-xs font-medium text-stone-700 transition hover:bg-stone-50"
									>
										<span
											className={cn(
												'flex size-4 shrink-0 items-center justify-center rounded border',
												checked
													? 'border-stone-900 bg-stone-900 text-white'
													: 'border-stone-300 bg-white',
											)}
											aria-hidden
										>
											{checked ? '✓' : ''}
										</span>
										<span className="min-w-0 truncate">
											{option.label}
										</span>
									</button>
								);
							},
						)}
					</div>
				</AnchoredDropdownMenu>
			</div>
		</div>
	);
};

export { ProfessionalAdminFilters };
